import { t } from "../../i18n";
import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import type { PreviewAsset } from "../game-assets/GameAssetPreview.types";

function visibleModelBounds(root: THREE.Object3D) {
  const bounds = new THREE.Box3();
  root.updateWorldMatrix(true, true);
  root.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (child.visible && mesh.isMesh && mesh.geometry)
      bounds.expandByObject(mesh, true);
  });
  return bounds;
}

export function ModelPreview({ asset }: { asset: PreviewAsset }) {
  const extension = asset.name.split(".").pop()?.toLowerCase();
  const hostRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (extension !== "glb" && extension !== "gltf") return;
    const host = hostRef.current;
    if (!host) return;
    setError("");
    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#0f172a");
    const camera = new THREE.PerspectiveCamera(35, 1, 0.01, 1_000);
    camera.position.set(2.5, 1.8, 3.5);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    host.replaceChildren(renderer.domElement);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.target.set(0, 0.5, 0);
    scene.add(new THREE.HemisphereLight(0xffffff, 0x334155, 2.2));
    const keyLight = new THREE.DirectionalLight(0xffffff, 3);
    keyLight.position.set(3, 5, 4);
    scene.add(keyLight);
    scene.add(new THREE.GridHelper(10, 20, 0x475569, 0x1e293b));
    const resize = () => {
      const width = host.clientWidth || 320;
      camera.aspect = width / 280;
      camera.updateProjectionMatrix();
      renderer.setSize(width, 280, false);
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    let frame = 0;
    const render = () => {
      controls.update();
      renderer.render(scene, camera);
      frame = requestAnimationFrame(render);
    };
    render();
    new GLTFLoader().load(
      asset.url,
      (loaded) => {
        const modelRoot = new THREE.Group();
        modelRoot.add(loaded.scene);
        scene.add(modelRoot);
        const bounds = visibleModelBounds(modelRoot);
        if (bounds.isEmpty()) {
          setError("This model does not contain visible geometry.");
          return;
        }
        const size = bounds.getSize(new THREE.Vector3());
        const center = bounds.getCenter(new THREE.Vector3());
        const largest = Math.max(size.x, size.y, size.z, 0.01);
        modelRoot.position.set(-center.x, -center.y, -center.z);
        modelRoot.scale.setScalar(1.8 / largest);
        modelRoot.updateWorldMatrix(true, true);
        const fittedBounds = visibleModelBounds(modelRoot);
        const fittedCenter = fittedBounds.getCenter(new THREE.Vector3());
        const fittedSphere = fittedBounds.getBoundingSphere(new THREE.Sphere());
        const viewDirection = new THREE.Vector3(2.6, 1.8, 3.4).normalize();
        const distance = Math.max(fittedSphere.radius * 2.8, 0.8);
        controls.target.copy(fittedCenter);
        camera.position
          .copy(fittedCenter)
          .add(viewDirection.multiplyScalar(distance));
        camera.lookAt(fittedCenter);
        camera.near = Math.max(fittedSphere.radius / 100, 0.001);
        camera.far = Math.max(fittedSphere.radius * 100, 1_000);
        camera.updateProjectionMatrix();
        controls.update();
        camera.lookAt(fittedCenter);
      },
      undefined,
      () => setError("This model could not be loaded for interactive preview."),
    );
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      controls.dispose();
      scene.traverse((object) => {
        const mesh = object as THREE.Mesh;
        mesh.geometry?.dispose?.();
        if (Array.isArray(mesh.material))
          mesh.material.forEach((material) => material.dispose());
        else (mesh.material as THREE.Material | undefined)?.dispose?.();
      });
      renderer.dispose();
      host.replaceChildren();
    };
  }, [asset.url, asset.name, extension]);

  if (extension !== "glb" && extension !== "gltf")
    return (
      <div className="grid min-h-24 place-items-center rounded-xl border border-dashed border-stone-300 bg-stone-50 px-4 py-5 text-center text-xs text-stone-500 dark:border-white/15 dark:bg-white/[0.03] dark:text-stone-400">
        {t(
          "Interactive preview is available for GLB and GLTF. Use the download link below to open this {{extension}} asset in an external tool.",
          { extension: extension?.toUpperCase() || "3D" },
        )}
      </div>
    );
  return (
    <div className="overflow-hidden rounded-xl border border-slate-700 bg-slate-950">
      <div
        ref={hostRef}
        className="h-[280px] w-full"
        aria-label={t("Interactive 3D model preview")}
      />
      {error && (
        <div className="border-t border-slate-700 px-3 py-2 text-xs text-slate-300">
          {error}
        </div>
      )}
    </div>
  );
}
